import styled from 'styled-components';
import { motion, useInView, useMotionValue, useAnimationFrame } from 'framer-motion';
import { useRef, useState } from 'react';

const mvp = [
  { label: 'Misión', text: 'Fabricar tarimas que faciliten el desplazamiento de productos, brindando suministro garantizado y calidad consistente en cada entrega.' },
  { label: 'Visión', text: 'Ser la empresa líder en fabricación de tarimas a nivel regional, reconocida por nuestros estándares de higiene y calidad industrial.' },
  { label: 'Propósito', text: 'Crear y garantizar soluciones para movimiento de productos, adaptándonos a cada cliente con consistencia y orden en cada proceso.' },
];

const valores = [
  'Las personas somos lo más importante.',
  'Hacemos lo correcto, lo hacemos bien.',
  'Escucho, entiendo y atiendo.',
  'Rapidez que sorprenda.',
  'Limpieza y orden en cada etapa.',
  'Crecer, crecer, siempre crecer.',
];

const CARD_W = 420;
const GAP = 20;

const slides = [
  { id: 1, src: '/img/fabrica/fabrica-01.jpg', label: 'Planta de producción' },
  { id: 2, src: '/img/fabrica/fabrica-02.jpg', label: 'Área de maquila' },
  { id: 3, src: '/img/fabrica/fabrica-03.jpg', label: 'Instalaciones' },
  { id: 4, src: '/img/fabrica/fabrica-04.jpg', label: 'Producción' },
  { id: 5, src: '/img/fabrica/fabrica-05.jpg', label: 'Almacén de tarimas' },
  { id: 6, src: '/img/fabrica/fabrica-06.jpg', label: 'Fábrica' },
  { id: 7, src: '/img/fabrica/fabrica-07.jpg', label: 'Proceso de fabricación' },
  { id: 8, src: '/img/fabrica/fabrica-08.jpg', label: 'Control de calidad' },
  { id: 9, src: '/img/fabrica/fabrica-09.jpg', label: 'Instalaciones' },
];

function FadeUp({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  const trackRef = useRef(null);
  const x = useMotionValue(0);
  const [paused, setPaused] = useState(false);
  const SPEED = 0.6; // px por frame
  const TOTAL = (CARD_W + GAP) * slides.length;

  useAnimationFrame(() => {
    if (paused) return;
    const current = x.get();
    const next = current - SPEED;
    // Cuando llegue al final, regresa suavemente al inicio
    x.set(next <= -(TOTAL / 2) ? 0 : next);
  });

  return (
    <Section id="about">
      <Inner>
        {/* Header */}
        <FadeUp>
          <SectionTag>Sobre Nosotros</SectionTag>
          <SectionTitle>
            <span>Más de 20 años</span> fabricando tarimas con
            <AccentWord> calidad y compromiso</AccentWord>
          </SectionTitle>
          <SectionLead>
            En Grupo Jaycom nos especializamos en la maquila de tarimas de madera, ofreciendo
            soluciones confiables para empresas que requieren consistencia, calidad y cumplimiento
            en cada entrega. Operamos bajo procesos que priorizan la limpieza, el orden y el
            control en cada etapa de producción.
          </SectionLead>
        </FadeUp>

        {/* Mission / Vision / Purpose */}
        <MVPSection>
          {mvp.map((it, i) => (
            <FadeUp key={it.label} delay={i * 0.12}>
              <MVPItem>
                <MVPLabel>{it.label}</MVPLabel>
                <MVPText>{it.text}</MVPText>
              </MVPItem>
            </FadeUp>
          ))}
        </MVPSection>

        {/* Values */}
        <ValuesBlock>
          <FadeUp>
            <ValuesTitle>Nuestros Valores</ValuesTitle>
            <ValuesGrid>
              {valores.map((v, i) => (
                <ValueItem key={i}>
                  <Num>0{i + 1}</Num>
                  <span>{v}</span>
                </ValueItem>
              ))}
            </ValuesGrid>
          </FadeUp>
        </ValuesBlock>

        {/* Gallery carousel */}
        <FadeUp delay={0.1}>
          <GalleryTitle>Instalaciones</GalleryTitle>
          <CarouselWrapper
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <CarouselTrack
              ref={trackRef}
              style={{ x }}
              drag="x"
              dragConstraints={{ right: 0, left: -((CARD_W + GAP) * slides.length) }}
              dragTransition={{ bounceStiffness: 300, bounceDamping: 30 }}
              onDragStart={() => setPaused(true)}
              onDragEnd={() => setPaused(false)}
              whileTap={{ cursor: 'grabbing' }}
            >
              {/* Duplicamos para loop infinito */}
              {[...slides, ...slides].map((s, i) => (
                <CarouselCard key={i}>
                  <CardImg src={s.src} alt={s.label} />
                </CarouselCard>
              ))}
            </CarouselTrack>
          </CarouselWrapper>
        </FadeUp>
      </Inner>
    </Section>
  );
}

const Section = styled.section`
  background: transparent;
  padding: 7rem 2rem;
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionTag = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--blue);
  border-left: 3px solid var(--red);
  padding-left: 0.75rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3.5rem);
  line-height: 1.1;
  margin-bottom: 1.5rem;
  max-width: 720px;
  color: var(--blue);
  span { color: var(--red); text-shadow: var(--shadow-3d); }
`;

const AccentWord = styled.span`
  color: var(--red);
  text-shadow: var(--shadow-3d);
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-sub);
  margin-bottom: 4rem;
`;

const MVPSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-bottom: 0;
  border-top: 2px solid var(--blue);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MVPItem = styled.div`
  padding: 3rem 2.5rem 3rem 0;
  border-right: 1px solid var(--border);
  &:last-child { border-right: none; padding-right: 0; }
  &:not(:first-child) { padding-left: 2.5rem; padding-right: 2.5rem; }
  &:last-child { padding-left: 2.5rem; }

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid var(--border);
    padding: 2rem 0;
    &:last-child { border-bottom: none; }
  }
`;

const MVPLabel = styled.p`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 3.5rem;
  color: var(--red) !important; 
  line-height: 1;
  margin-bottom: 1.25rem;
  letter-spacing: 0.02em;
  text-shadow:
    1px 1px 0 #F5C000,
    2px 2px 0 #E0A800,
    3px 3px 0 #C89000,
    4px 4px 0 rgba(180,120,0,0.3);
`;

const MVPText = styled.p`
  font-size: 0.92rem;
  line-height: 1.8;
  color: var(--text-sub);
  max-width: 340px;
`;

const ValuesBlock = styled.div`
  margin-bottom: 5rem;
  padding: 5rem 0;
`;

const ValuesTitle = styled.h3`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2.2rem;
  letter-spacing: 0.06em;
  margin-bottom: 2.5rem;
  color: var(--blue);
`;

const ValuesGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem 3rem;

  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const ValueItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  span {
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.55;
    color: var(--text-sub);
  }
`;

const Num = styled.span`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 3rem !important;
  color: var(--red) !important;
  line-height: 1;
  text-shadow:
    1px 1px 0 #F5C000,
    2px 2px 0 #E0A800,
    3px 3px 0 #C89000,
    4px 4px 0 rgba(180,120,0,0.25);
`;

const GalleryTitle = styled.h3`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2rem;
  color: var(--blue);
  margin-bottom: 1.5rem;
  letter-spacing: 0.05em;
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

const CarouselTrack = styled(motion.div)`
  display: flex;
  gap: ${GAP}px;
  width: max-content;
  padding: 0.5rem 0 1rem;
  user-select: none;
`;

const CarouselCard = styled.div`
  width: ${CARD_W}px;
  flex-shrink: 0;
`;

const CardImg = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  display: block;
`;

const CardLabel = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-sub);
  margin-top: 0.5rem;
`;
